<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ExternalLink, Check } from 'lucide-vue-next'
import type { BridgeLink } from '@/types/bridge'
import { isBridgeUrlSafe, isProductionBridgeUrl, isSandboxEnvironment } from '@/config/bridge'

const isProdData = (url: string | null | undefined) => isSandboxEnvironment() && isProductionBridgeUrl(url)

const props = defineProps<{
  link: BridgeLink | null
}>()

const emit = defineEmits<{
  (e: 'refresh'): void
}>()

const tosCompleted = computed(() => props.link?.tos_status === 'approved')
const kycCompleted = computed(() => props.link?.kyc_status === 'approved')
const allCompleted = computed(() => tosCompleted.value && kycCompleted.value)

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && !allCompleted.value) {
    emit('refresh')
  }
}

onMounted(() => document.addEventListener('visibilitychange', onVisibilityChange))
onUnmounted(() => document.removeEventListener('visibilitychange', onVisibilityChange))

const openTos = () => {
  if (props.link?.tos_url) window.open(props.link.tos_url, '_blank', 'noopener,noreferrer')
}

const openKyc = () => {
  if (props.link?.kyc_url) window.open(props.link.kyc_url, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <div class="space-y-4">
    <!-- Steps -->
    <div class="space-y-2">
      <!-- Step 1: ToS -->
    <div
      class="flex items-center gap-3 p-3 rounded-lg"
      :class="tosCompleted ? 'bg-green-50' : ''"
    >
      <div
        class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
        :class="tosCompleted ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'"
      >
        <Check v-if="tosCompleted" class="h-4 w-4" />
        <span v-else>1</span>
      </div>
      <div class="flex-1 min-w-0">
        <p class="font-medium text-base" :class="tosCompleted ? 'text-green-700' : ''">
          Accept Terms of Service
        </p>
        <p class="text-base text-muted-foreground">Opens in a new tab. Close it when finished and return here.</p>
      </div>
      <div class="flex-shrink-0 flex flex-col items-end gap-1">
        <Badge v-if="tosCompleted" class="bg-green-100 text-green-700 border-green-200">Done</Badge>
        <template v-else>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button size="sm" @click="openTos" :disabled="!isBridgeUrlSafe(link?.tos_url)">
                  Open <ExternalLink class="h-3 w-3 ml-1" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Opens in a new tab</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span v-if="isProdData(link?.tos_url)" class="text-[10px] text-muted-foreground/60 font-medium tracking-wide">Prod data</span>
        </template>
      </div>
    </div>

    <!-- Step 2: KYC -->
    <div
      class="flex items-center gap-3 p-3 rounded-lg"
      :class="kycCompleted ? 'bg-green-50' : ''"
    >
      <div
        class="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
        :class="kycCompleted ? 'bg-green-100 text-green-600' : tosCompleted ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-400'"
      >
        <Check v-if="kycCompleted" class="h-4 w-4" />
        <span v-else>2</span>
      </div>
      <div class="flex-1 min-w-0">
        <p class="font-medium text-base" :class="kycCompleted ? 'text-green-700' : !tosCompleted ? 'text-gray-400' : ''">
          Identity Verification
        </p>
        <p class="text-base" :class="!tosCompleted ? 'text-gray-400' : 'text-muted-foreground'">
          {{ !tosCompleted ? 'Complete step 1 first' : 'Verify your identity to enable payments' }}
        </p>
      </div>
      <div class="flex-shrink-0 flex flex-col items-end gap-1">
        <Badge v-if="kycCompleted" class="bg-green-100 text-green-700 border-green-200">Done</Badge>
        <template v-else>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <Button size="sm" @click="openKyc" :disabled="!tosCompleted || !isBridgeUrlSafe(link?.kyc_url)">
                  Verify <ExternalLink class="h-3 w-3 ml-1" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Opens in a new tab</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <span v-if="isProdData(link?.kyc_url)" class="text-[10px] text-muted-foreground/60 font-medium tracking-wide">Prod data</span>
        </template>
      </div>
    </div>
    </div>
  </div>
</template>
